// Picks vendors from the shared directory for a plan's area and strategies.
// Pure: the caller passes in what the directory holds. Email and phone only
// ever appear inside the rep object.
import type { DefenseStrategy, Vendor, VendorRep } from '../types';
import { strategiesForCategory } from '../constants/vendorCategories';
import { isNationalVendor, vendorCoversArea } from './vendors';

export interface VendorSuggestion {
  vendor: Vendor;
  rep?: VendorRep;
  matchedCategories: string[];
  matchedStrategies: DefenseStrategy[];
  /** True when the vendor lists this area by id rather than working everywhere. */
  local: boolean;
}

const byName = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { sensitivity: 'base' });

/** The vendor's best approved rep: one listed for this area first, then any. */
function bestRep(reps: VendorRep[], vendorId: string, areaId: string): VendorRep | undefined {
  const mine = reps.filter((r) => r.vendorId === vendorId && r.status === 'approved');
  const local = mine.filter((r) => r.areaIds.includes(areaId));
  const pool = local.length > 0 ? local : mine;
  return [...pool].sort((a, b) => byName(a.name, b.name))[0];
}

function build(
  vendor: Vendor,
  reps: VendorRep[],
  areaId: string,
  strategyIds: DefenseStrategy[]
): VendorSuggestion {
  const matchedCategories: string[] = [];
  const hit = new Set<DefenseStrategy>();
  for (const category of vendor.categories) {
    const wanted = strategiesForCategory(category).filter((s) => strategyIds.includes(s));
    if (wanted.length === 0) continue;
    matchedCategories.push(category);
    for (const s of wanted) hit.add(s);
  }
  const suggestion: VendorSuggestion = {
    vendor,
    matchedCategories,
    matchedStrategies: strategyIds.filter((s) => hit.has(s)),
    local: !isNationalVendor(vendor),
  };
  const rep = bestRep(reps, vendor.id, areaId);
  if (rep) suggestion.rep = rep;
  return suggestion;
}

function approvedForArea(vendors: Vendor[], areaId: string): Vendor[] {
  return vendors.filter((v) => v.status === 'approved' && vendorCoversArea(v, areaId));
}

export function suggestVendors(
  vendors: Vendor[],
  reps: VendorRep[],
  opts: { areaId: string; strategyIds: DefenseStrategy[] }
): VendorSuggestion[] {
  return approvedForArea(vendors, opts.areaId)
    .map((v) => build(v, reps, opts.areaId, opts.strategyIds))
    .filter((s) => s.matchedStrategies.length > 0)
    .sort(
      (a, b) =>
        Number(b.local) - Number(a.local) ||
        b.matchedStrategies.length - a.matchedStrategies.length ||
        byName(a.vendor.organization, b.vendor.organization)
    );
}

/** Every approved vendor covering the area, for the "All vendors" list. */
export function vendorsForArea(
  vendors: Vendor[],
  reps: VendorRep[],
  areaId: string
): VendorSuggestion[] {
  return approvedForArea(vendors, areaId)
    .map((v) => build(v, reps, areaId, []))
    .sort(
      (a, b) =>
        Number(b.local) - Number(a.local) || byName(a.vendor.organization, b.vendor.organization)
    );
}
